import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { getTripById, updateTrip } from '../store/tripSlice';
import useSocket from '../hooks/useSocket';
import tripService from '../services/tripService';
import BudgetPlanner from '../components/BudgetPlanner';
import { 
  Calendar, 
  MapPin, 
  Users, 
  Share2, 
  Plus, 
  Clock,
  Mail,
  Copy,
  DollarSign,
} from 'lucide-react';

const TripDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentTrip, loading } = useSelector(state => state.trips);
  const { user } = useSelector(state => state.auth);
  const { emitTripUpdate } = useSocket(id);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('viewer');
  const [shareLink, setShareLink] = useState('');
  const [editingDay, setEditingDay] = useState(null);
  const [activeTab, setActiveTab] = useState('itinerary');
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', message: '' });
  const [newPlace, setNewPlace] = useState({
    name: '',
    address: '',
    activities: '',
    timing: '',
    notes: ''
  });

  useEffect(() => {
    dispatch(getTripById(id));
  }, [dispatch, id]);

  const canEdit = currentTrip && (
    currentTrip.owner._id === user._id ||
    currentTrip.participants.some(p => 
      p.user._id === user._id && p.role === 'editor'
    )
  );

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      await tripService.inviteUser(id, inviteEmail, inviteRole);
      setShowInviteModal(false);
      setInviteEmail('');
      dispatch(getTripById(id));
      toast.success('User invited successfully!');
    } catch (error) {
      toast.error('Failed to invite user');
    }
  };

  const handleGenerateShareLink = async () => {
    try {
      const result = await tripService.generateShareLink(id);
      setShareLink(result.shareLink);
      toast.success('Share link generated!');
    } catch (error) {
      toast.error('Failed to generate share link');
    }
  };

  const handleCopyShareLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Link copied to clipboard!');
  };

  const handleShareViaEmail = async () => {
    try {
      await tripService.shareViaEmail(id, emailData.email, emailData.message);
      setShowEmailModal(false);
      setEmailData({ email: '', message: '' });
      toast.success('📧 Trip shared via email!');
    } catch (error) {
      toast.error('Failed to share via email');
    }
  };

  const handleAddPlace = async (day) => {
    if (!newPlace.name) return;

    try {
      const updatedItinerary = [...currentTrip.itinerary];
      const dayIndex = updatedItinerary.findIndex(item => item.day === day);
      
      const place = {
        ...newPlace,
        activities: newPlace.activities.split(',').map(a => a.trim()).filter(a => a)
      };

      if (dayIndex >= 0) {
        updatedItinerary[dayIndex].places.push(place);
      } else {
        updatedItinerary.push({
          day,
          places: [place]
        });
      }

      const updatedTrip = await dispatch(updateTrip({
        id,
        tripData: { itinerary: updatedItinerary }
      })).unwrap();

      emitTripUpdate(updatedTrip);
      setEditingDay(null);
      setNewPlace({ name: '', address: '', activities: '', timing: '', notes: '' });
      toast.success('Place added successfully!');
    } catch (error) {
      toast.error('Failed to add place');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!currentTrip) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Trip not found</h2>
          <button onClick={() => navigate('/dashboard')} className="btn-primary">
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Trip Header */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{currentTrip.name}</h1>
            <p className="text-gray-600 mb-4">{currentTrip.description}</p>
            
            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              <div className="flex items-center">
                <Calendar className="h-4 w-4 mr-1" />
                <span>
                  {format(new Date(currentTrip.startDate), 'MMM dd')} - {format(new Date(currentTrip.endDate), 'MMM dd, yyyy')}
                </span>
              </div>
              <div className="flex items-center">
                <MapPin className="h-4 w-4 mr-1" />
                <span>{currentTrip.totalDays} days</span>
              </div>
              <div className="flex items-center">
                <Users className="h-4 w-4 mr-1" />
                <span>{currentTrip.participants.length + 1} travelers</span>
              </div>
            </div>
          </div>

          {canEdit && (
            <div className="flex space-x-2">
              <button
                onClick={() => setShowEmailModal(true)}
                className="btn-success flex items-center space-x-1"
              >
                <Mail className="h-4 w-4" />
                <span>📧 Email</span>
              </button>
              <button
                onClick={handleGenerateShareLink}
                className="btn-secondary flex items-center space-x-1"
              >
                <Share2 className="h-4 w-4" />
                <span>Share</span>
              </button>
            </div>
          )}
        </div>

        {/* Participants */}
        <div className="flex items-center space-x-4">
          <span className="text-sm font-medium text-gray-700">Travelers:</span>
          <div className="flex items-center space-x-2">
            <img
              src={`https://ui-avatars.com/api/?name=${currentTrip.owner.name}&size=32&background=3b82f6&color=fff`}
              alt={currentTrip.owner.name}
              className="w-8 h-8 rounded-full"
              title={`${currentTrip.owner.name} (Owner)`}
            />
            {currentTrip.participants.map((participant) => (
              <img
                key={participant.user._id}
                src={`https://ui-avatars.com/api/?name=${participant.user.name}&size=32&background=6b7280&color=fff`}
                alt={participant.user.name}
                className="w-8 h-8 rounded-full"
                title={`${participant.user.name} (${participant.role})`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Share Link */}
      {shareLink && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium text-blue-900">Share Link Generated</h3>
              <p className="text-sm text-blue-700 break-all">{shareLink}</p>
            </div>
            <button
              onClick={handleCopyShareLink}
              className="btn-primary flex items-center space-x-1"
            >
              <Copy className="h-4 w-4" />
              <span>Copy</span>
            </button>
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="bg-white rounded-2xl shadow-lg p-2 mb-8">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('itinerary')}
            className={`flex-1 py-3 px-6 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2 ${
              activeTab === 'itinerary'
                ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white shadow-lg'
                : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
            }`}
          >
            <Calendar className="h-5 w-5" />
            <span>🗓️ Itinerary</span>
          </button>
          <button
            onClick={() => setActiveTab('budget')}
            className={`flex-1 py-3 px-6 rounded-xl font-medium transition-all duration-300 flex items-center justify-center space-x-2 ${
              activeTab === 'budget'
                ? 'bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg'
                : 'text-gray-600 hover:text-green-600 hover:bg-green-50'
            }`}
          >
            <DollarSign className="h-5 w-5" />
            <span>💰 Budget</span>
          </button>

        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'itinerary' && (
        <div className="space-y-6">
          <h2 className="text-2xl font-bold text-gray-900">🗓️ Trip Itinerary</h2>
        
        {Array.from({ length: currentTrip.totalDays }, (_, index) => {
          const day = index + 1;
          const dayItinerary = currentTrip.itinerary.find(item => item.day === day);
          
          return (
            <div key={day} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-gray-900">Day {day}</h3>
                {canEdit && (
                  <button
                    onClick={() => setEditingDay(day)}
                    className="btn-secondary flex items-center space-x-1"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Place</span>
                  </button>
                )}
              </div>

              {dayItinerary?.places?.length > 0 ? (
                <div className="space-y-4">
                  {dayItinerary.places.map((place, placeIndex) => (
                    <div key={placeIndex} className="border-l-4 border-primary-500 pl-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-900">{place.name}</h4>
                          {place.address && (
                            <p className="text-sm text-gray-600 flex items-center mt-1">
                              <MapPin className="h-3 w-3 mr-1" />
                              {place.address}
                            </p>
                          )}
                          {place.timing && (
                            <p className="text-sm text-gray-600 flex items-center mt-1">
                              <Clock className="h-3 w-3 mr-1" />
                              {place.timing}
                            </p>
                          )}
                          {place.activities?.length > 0 && (
                            <div className="mt-2">
                              <span className="text-sm font-medium text-gray-700">Activities: </span>
                              <span className="text-sm text-gray-600">{place.activities.join(', ')}</span>
                            </div>
                          )}
                          {place.notes && (
                            <p className="text-sm text-gray-600 mt-2">{place.notes}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No places added for this day</p>
              )}

              {/* Add Place Form */}
              {editingDay === day && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-4">Add New Place</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input
                      type="text"
                      placeholder="Place name"
                      value={newPlace.name}
                      onChange={(e) => setNewPlace({ ...newPlace, name: e.target.value })}
                      className="input-field"
                    />
                    <input
                      type="text"
                      placeholder="Address"
                      value={newPlace.address}
                      onChange={(e) => setNewPlace({ ...newPlace, address: e.target.value })}
                      className="input-field"
                    />
                    <input
                      type="text"
                      placeholder="Activities (comma separated)"
                      value={newPlace.activities}
                      onChange={(e) => setNewPlace({ ...newPlace, activities: e.target.value })}
                      className="input-field"
                    />
                    <input
                      type="text"
                      placeholder="Timing"
                      value={newPlace.timing}
                      onChange={(e) => setNewPlace({ ...newPlace, timing: e.target.value })}
                      className="input-field"
                    />
                    <textarea
                      placeholder="Notes"
                      value={newPlace.notes}
                      onChange={(e) => setNewPlace({ ...newPlace, notes: e.target.value })}
                      className="input-field md:col-span-2"
                      rows="2"
                    />
                  </div>
                  <div className="flex justify-end space-x-2 mt-4">
                    <button
                      onClick={() => setEditingDay(null)}
                      className="btn-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleAddPlace(day)}
                      className="btn-primary"
                    >
                      Add Place
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        </div>
      )}

      {/* Budget Tab */}
      {activeTab === 'budget' && (
        <div className="animate-fade-in">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">💰 Budget Planner</h2>
          <BudgetPlanner tripId={id} />
        </div>
      )}



      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold mb-4">Invite User</h3>
            <form onSubmit={handleInviteUser}>
              <div className="space-y-4">
                <input
                  type="email"
                  placeholder="Email address"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="input-field"
                  required
                />
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value)}
                  className="input-field"
                >
                  <option value="viewer">Viewer</option>
                  <option value="editor">Editor</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Send Invite
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Email Share Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md mx-4">
            <h3 className="text-xl font-bold mb-4 flex items-center">
              <Mail className="h-6 w-6 mr-2 text-blue-600" />
              📧 Share Trip via Email
            </h3>
            <div className="space-y-4">
              <input
                type="email"
                placeholder="Recipient's email"
                value={emailData.email}
                onChange={(e) => setEmailData({...emailData, email: e.target.value})}
                className="input-field"
                required
              />
              <textarea
                placeholder="Add a personal message (optional)"
                value={emailData.message}
                onChange={(e) => setEmailData({...emailData, message: e.target.value})}
                className="input-field"
                rows="3"
              />
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => setShowEmailModal(false)}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleShareViaEmail}
                className="btn-success"
              >
                🚀 Send Email
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TripDetail;